import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PropertyCard } from "@/components/PropertyCard";
import { useAuth } from "@/context/AuthContext";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Heart, Loader2 } from "lucide-react";

export default function Favourites() {
  const { user } = useAuth();

  const { data = [], isLoading } = useQuery({
    queryKey: ["favourites", (user?.favourites || []).join(",")],
    queryFn: async () => (await api.get("/favourites")).data,
    enabled: !!user,
  });

  if (!user) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-24 text-center" data-testid="fav-login-prompt">
        <Heart className="w-10 h-10 mx-auto mb-3 text-slate-300" />
        <p className="text-slate-500">Please log in to view your saved properties.</p>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-8" data-testid="favourites-page">
      <Seo title="Saved Properties | Dotlands Realty" description="Your shortlisted agricultural land, plots and homes on Dotlands Realty." path="/favourites" noindex />

      <div className="flex items-center gap-2 mb-1">
        <Heart className="w-6 h-6 text-red-500 fill-red-500" />
        <h1 className="text-2xl sm:text-3xl font-heading font-bold text-slate-900">Saved properties</h1>
      </div>
      <p className="text-slate-500 mb-6">Your shortlisted land, plots and homes — all in one place.</p>

      {isLoading ? (
        <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></div>
      ) : data.length === 0 ? (
        <div className="text-center py-24 text-slate-500" data-testid="fav-empty">
          <Heart className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          <p>You haven't saved any properties yet.</p>
          <Button asChild className="mt-4 bg-emerald-600 hover:bg-emerald-700 rounded-full">
            <Link to="/properties" data-testid="fav-browse-btn">Browse properties</Link>
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {data.map((p, i) => <PropertyCard key={p.id} p={p} index={i} />)}
        </div>
      )}
    </div>
  );
}
